"use client";

import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../Components/ui/dialog";
import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Label } from "../Components/ui/label";
import { ChevronDown } from "lucide-react";

export function EditVehiclesDialog({ isOpen, onClose, vehicles, onSave }) {
  const [vehiclesData, setVehiclesData] = useState(
    vehicles.map((vehicle) => ({
      make: vehicle.make || "",
      model: vehicle.model || "",
      year: vehicle.year || "",
      vin: vehicle.vin || "",
      lot_number: vehicle.lot_number || "",
      buyer_number: vehicle.buyer_number || "",
    }))
  );

  const [selectedVehicle, setSelectedVehicle] = useState("0");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (key, value) => {
    const updatedVehicles = [...vehiclesData];
    updatedVehicles[Number.parseInt(selectedVehicle)][key] = value;
    setVehiclesData(updatedVehicles);
  };

  // Handle save
  const handleSave = () => {
    onSave(vehiclesData);
    onClose();
  };

  // Generate vehicle display names for the selector
  const getVehicleDisplayName = (vehicle, index) => {
    if (vehicle.make && vehicle.model) {
      return `${vehicle.make} ${vehicle.model} ${vehicle.year || ""}`;
    } else if (vehicle.vin) {
      return `Vehicle ${index + 1} - VIN: ${vehicle.vin}`;
    } else {
      return `Vehicle ${index + 1}`;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Show Vehicle Details
          </DialogTitle>
        </DialogHeader>

        {/* Custom Vehicle Selector */}
        <div className="mt-4 mb-6">
          <Label
            htmlFor="vehicle-selector"
            className="text-sm font-medium mb-2 block"
          >
            Select Vehicle to Show Details
          </Label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex text-white items-center justify-between w-full h-10 px-3 py-2 text-left bg-[#1c1b19] border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors hover:bg-[#252422]"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen}
            >
              <span>
                {getVehicleDisplayName(
                  vehiclesData[Number.parseInt(selectedVehicle)],
                  Number.parseInt(selectedVehicle)
                )}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform text-white duration-200 ${
                  isDropdownOpen ? "transform rotate-180" : ""
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-[#252422] border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                <ul className="py-1" role="listbox">
                  {vehiclesData.map((vehicle, index) => (
                    <li
                      key={index}
                      role="option"
                      aria-selected={selectedVehicle === index.toString()}
                      className={`px-3 py-2 cursor-pointer  hover:bg-[#333] ${
                        selectedVehicle === index.toString()
                          ? "bg-amber-900/30 text-amber-500"
                          : "text-white"
                      }`}
                      onClick={() => {
                        setSelectedVehicle(index.toString());
                        setIsDropdownOpen(false);
                      }}
                    >
                      {getVehicleDisplayName(vehicle, index)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Details Form */}
        <div className="bg-[#252422] rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-white mb-4 border-b border-gray-700 pb-2">
            {getVehicleDisplayName(
              vehiclesData[Number.parseInt(selectedVehicle)],
              Number.parseInt(selectedVehicle)
            )}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(vehiclesData[Number.parseInt(selectedVehicle)]).map(
              ([key, value]) => (
                <div className="relative" key={key}>
                  <Label
                    className="absolute -top-2 text-[0.7rem] bg-[#252422] px-1 left-2 z-10"
                    htmlFor={`${key}-${selectedVehicle}`}
                  >
                    {key
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`${key}-${selectedVehicle}`}
                    value={value}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    className="h-[3rem] bg-[#1c1b19] border-gray-700"
                    disabled
                  />
                </div>
              )
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            onClick={() => {
              const prevIndex =
                (Number.parseInt(selectedVehicle) - 1 + vehiclesData.length) %
                vehiclesData.length;
              setSelectedVehicle(prevIndex.toString());
            }}
            className="text-sm"
            disabled={vehiclesData.length <= 1}
          >
            Previous Vehicle
          </Button>

          <div className="text-sm text-gray-400">
            {Number.parseInt(selectedVehicle) + 1} of {vehiclesData.length}
          </div>

          <Button
            variant="outline"
            onClick={() => {
              const nextIndex =
                (Number.parseInt(selectedVehicle) + 1) % vehiclesData.length;
              setSelectedVehicle(nextIndex.toString());
            }}
            className="text-sm"
            disabled={vehiclesData.length <= 1}
          >
            Next Vehicle
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
